import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Copy, Save, PlayCircle, X } from "lucide-react";
import { VideoPlayer } from "@/components/VideoPlayer";

const DAYS = ["Day 1", "Day 2", "Day 3", "Day 4", "Day 5", "Day 6", "Day 7"];

export default function CoachProgramBuilder() {
  const { programId } = useParams();
  const [program, setProgram] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [week, setWeek] = useState(1);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/programs/${programId}`).then((r) => {
      setProgram(r.data);
      setWorkouts(r.data.workouts || []);
    }).catch(() => toast.error("Program not found"));
    api.get("/exercises").then((r) => setExercises(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- api is a stable module import; setters are stable
  }, [programId]);

  const exById = useMemo(() => Object.fromEntries(exercises.map((e) => [e.exercise_id, e])), [exercises]);
  const getWorkout = (w, d) => workouts.find((x) => x.week === w && x.day === d);

  const updateWorkout = (w, d, patch) => {
    setWorkouts((prev) => prev.map((x) => (x.week === w && x.day === d ? { ...x, ...patch } : x)));
  };

  const addWorkout = (w, d) => {
    setWorkouts((prev) => [...prev, { week: w, day: d, name: "Workout", exercises: [] }]);
  };

  const removeWorkout = (w, d) => {
    setWorkouts((prev) => prev.filter((x) => !(x.week === w && x.day === d)));
  };

  const addExercise = (w, d, exercise_id) => {
    if (!exercise_id) return;
    const ex = exById[exercise_id];
    const wo = getWorkout(w, d);
    updateWorkout(w, d, {
      exercises: [...(wo.exercises || []), { exercise_id, name: ex?.name || "", sets: 3, reps: "10", rest_seconds: 60, notes: "" }],
    });
  };

  const updateExercise = (w, d, idx, patch) => {
    const wo = getWorkout(w, d);
    const next = wo.exercises.map((e, i) => (i === idx ? { ...e, ...patch } : e));
    updateWorkout(w, d, { exercises: next });
  };

  const removeExercise = (w, d, idx) => {
    const wo = getWorkout(w, d);
    updateWorkout(w, d, { exercises: wo.exercises.filter((_, i) => i !== idx) });
  };

  const duplicateWeek = () => {
    if (!program || week >= program.duration_weeks) return;
    setWorkouts((prev) => {
      const cleaned = prev.filter((x) => x.week !== week + 1);
      const copies = prev.filter((x) => x.week === week).map((x) => ({ ...x, week: week + 1, exercises: x.exercises.map((e) => ({ ...e })) }));
      return [...cleaned, ...copies];
    });
    toast.success(`Week ${week} copied to week ${week + 1}`);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/programs/${programId}`, { workouts });
      toast.success("Program saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!program) return <div className="text-secondary-luxe">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="program-builder">
      <Link to="/coach/programs" className="btn-ghost" data-testid="back-to-programs"><ArrowLeft size={14} /> Programs</Link>
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">{program.program_type} · {program.duration_weeks} weeks</div>
          <h1 className="font-serif text-5xl mt-3">{program.title}</h1>
          <p className="text-secondary-luxe text-sm mt-3 max-w-2xl">Build each training day. Clients see videos and log sets, reps &amp; weight from their app.</p>
        </div>
        <div className="flex gap-3">
          <button className="btn-secondary" onClick={duplicateWeek} data-testid="duplicate-week-btn"><Copy size={13} /> Copy week → next</button>
          <button className="btn-primary" onClick={save} disabled={saving} data-testid="save-program-btn"><Save size={13} /> {saving ? "Saving…" : "Save program"}</button>
        </div>
      </header>

      <div className="flex gap-2 flex-wrap">
        {Array.from({ length: program.duration_weeks }, (_, i) => i + 1).map((w) => (
          <button key={w} onClick={() => setWeek(w)} data-testid={`week-tab-${w}`}
            className={w === week ? "btn-primary text-xs" : "btn-secondary text-xs"}>
            Week {w}
          </button>
        ))}
      </div>

      <div className="space-y-5">
        {DAYS.map((label, i) => {
          const d = i + 1;
          const wo = getWorkout(week, d);
          return (
            <div key={d} className="card-luxe" data-testid={`builder-day-${d}`}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="overline">{label}</div>
                {wo ? (
                  <button className="btn-ghost text-xs" onClick={() => removeWorkout(week, d)} data-testid={`remove-workout-${d}`}><Trash2 size={13} /> Remove day</button>
                ) : (
                  <button className="btn-secondary text-xs" onClick={() => addWorkout(week, d)} data-testid={`add-workout-${d}`}><Plus size={13} /> Add workout</button>
                )}
              </div>
              {!wo && <p className="text-xs text-tertiary-luxe mt-3">Rest day</p>}
              {wo && (
                <div className="mt-4 space-y-4">
                  <input className="input-luxe max-w-md" value={wo.name} placeholder="Workout name (e.g. Lower Body Strength)"
                    onChange={(e) => updateWorkout(week, d, { name: e.target.value })} data-testid={`workout-name-${d}`} />
                  <div className="space-y-3">
                    {(wo.exercises || []).map((ex, idx) => (
                      <div key={`${ex.exercise_id || ex.name || "ex"}-${idx}`} className="grid grid-cols-2 md:grid-cols-12 gap-3 items-end p-3" style={{ background: "var(--surface-2)" }}>
                        <div className="col-span-2 md:col-span-4">
                          <label className="label-luxe">Exercise</label>
                          <div className="flex items-center gap-2">
                            <span className="text-sm flex-1">{ex.name}</span>
                            {exById[ex.exercise_id]?.video_url && (
                              <button className="btn-ghost" onClick={() => setPreview(exById[ex.exercise_id])} data-testid={`preview-video-${d}-${idx}`}>
                                <PlayCircle size={15} style={{ color: "var(--accent)" }} />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="md:col-span-2">
                          <label className="label-luxe">Sets</label>
                          <input type="number" min="1" className="input-luxe" value={ex.sets}
                            onChange={(e) => updateExercise(week, d, idx, { sets: parseInt(e.target.value || "1", 10) })} data-testid={`sets-${d}-${idx}`} />
                        </div>
                        <div className="md:col-span-2">
                          <label className="label-luxe">Reps</label>
                          <input className="input-luxe" value={ex.reps}
                            onChange={(e) => updateExercise(week, d, idx, { reps: e.target.value })} data-testid={`reps-${d}-${idx}`} />
                        </div>
                        <div className="md:col-span-2">
                          <label className="label-luxe">Rest (s)</label>
                          <input type="number" min="0" className="input-luxe" value={ex.rest_seconds}
                            onChange={(e) => updateExercise(week, d, idx, { rest_seconds: parseInt(e.target.value || "0", 10) })} />
                        </div>
                        <div className="col-span-2 md:col-span-2 flex items-center gap-2">
                          <input className="input-luxe flex-1" placeholder="Notes" value={ex.notes || ""}
                            onChange={(e) => updateExercise(week, d, idx, { notes: e.target.value })} />
                          <button className="btn-ghost" onClick={() => removeExercise(week, d, idx)} data-testid={`remove-ex-${d}-${idx}`}><Trash2 size={13} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <select className="input-luxe max-w-sm" value="" onChange={(e) => addExercise(week, d, e.target.value)} data-testid={`add-exercise-select-${d}`}>
                    <option value="">+ Add exercise from library…</option>
                    {exercises.map((e) => <option key={e.exercise_id} value={e.exercise_id}>{e.name}{e.video_url ? " ▶" : ""}</option>)}
                  </select>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: "rgba(10,9,8,0.72)" }} onClick={() => setPreview(null)}>
          <div className="card-luxe max-w-xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-2xl">{preview.name}</h3>
              <button className="btn-ghost" onClick={() => setPreview(null)}><X size={16} /></button>
            </div>
            <VideoPlayer url={preview.video_url} />
          </div>
        </div>
      )}
    </div>
  );
}
